/** Replaces add-stream-picker/add-stream-picker.tsx on parked/data-viewer-followup-v2. */
import { type ComponentProps, type RefObject, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionHandler } from "verdi-web-client";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";
import { Button } from "@/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import type { PickerFilter } from "./picker-filter";
import { STREAM_PICKER_HEIGHT_PX, StreamPickerPanel } from "./stream-picker-panel";
import { usePickerCatalog } from "./use-picker-catalog";

const AddStreamIcon = iconRegistry[ICON_KEYS.ADD_DATA_STREAM];

/**
 * Only the list scrolls; zeroing the padding var stops a width jump while the popup opens. The popup
 * portals out of ViewerPane, so it sets the viewer's 4px spacing step again.
 */
export const STREAM_PICKER_POPUP_CLASS =
    "[--spacing:0.25rem] [&_[data-slot=popover-viewport]]:overflow-hidden [&_[data-slot=popover-viewport]]:py-0 [&_[data-slot=popover-viewport]]:[--viewport-inline-padding:0px]";

export function StreamPickerTrigger({
    label,
    triggerRef,
}: {
    label: string;
    triggerRef?: RefObject<HTMLButtonElement>;
}) {
    return (
        <PopoverTrigger
            render={<Button ref={triggerRef} aria-label={label} size={"icon"} tooltip={label} variant={"outline"} />}
        >
            <AddStreamIcon />
        </PopoverTrigger>
    );
}

type CollisionAvoidance = ComponentProps<typeof PopoverPopup>["collisionAvoidance"];

/** The popover's 4px offset and Base UI's 5px collision padding, on top of the panel. */
const ROOM_NEEDED_PX = STREAM_PICKER_HEIGHT_PX + 9;

/** Slides over the button when the panel fits neither above nor below; a capped panel hides its last rows. */
function collisionAvoidanceFor(trigger: HTMLElement | null): CollisionAvoidance {
    if (!trigger) return undefined;
    const { top, bottom } = trigger.getBoundingClientRect();
    if (Math.max(top, window.innerHeight - bottom) >= ROOM_NEEDED_PX) return undefined;
    return { side: "shift", align: "shift", fallbackAxisSide: "none" };
}

export function StreamPicker({
    zoneId,
    plottedStreams,
    addedIds,
    isPlottable,
    onAdd,
    onRemove,
    filter,
    onFilterChange,
}: {
    zoneId: string;
    plottedStreams: readonly ListDataStreams200Item[];
    addedIds: readonly number[];
    /** From the spec hook, so the picker offers only what the viewer can draw, hidden-by-default streams included. */
    isPlottable: (stream: ListDataStreams200Item) => boolean;
    onAdd: (streamId: number) => void;
    onRemove: (streamId: number) => void;
    filter: PickerFilter;
    onFilterChange: (filter: PickerFilter) => void;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    // Not lifted: the card and the expand dialog each mount a picker, and only one should open.
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [collisionAvoidance, setCollisionAvoidance] = useState<CollisionAvoidance>();
    const { plotted, catalog, status } = usePickerCatalog({
        enabled: open,
        zoneId,
        plottedStreams,
        isPlottable,
    });
    const added = useMemo(() => new Set(addedIds), [addedIds]);
    const label = t("button-label");

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                if (next) setCollisionAvoidance(collisionAvoidanceFor(triggerRef.current));
                setOpen(next);
            }}
        >
            <StreamPickerTrigger label={label} triggerRef={triggerRef} />
            <PopoverPopup
                align={"start"}
                className={STREAM_PICKER_POPUP_CLASS}
                collisionAvoidance={collisionAvoidance}
                side={"bottom"}
            >
                <StreamPickerPanel
                    addedIds={added}
                    catalog={catalog}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    filter={filter}
                    onFilterChange={onFilterChange}
                    isAdmin={Boolean(SessionHandler.admin)}
                    plotted={plotted}
                    status={status}
                />
            </PopoverPopup>
        </Popover>
    );
}
